import type { AuthProvisioningStatus } from "@elation/api-client/src/stitch-web";

export const PROVISIONING_POLL_INTERVAL_MS = 5_000;
export const PROVISIONING_NETWORK_BACKOFF_MAX_MS = 60_000;
export const PROVISIONING_PENDING_TIMEOUT_MS = 10 * 60 * 1_000;
export const PROVISIONING_SUPPORT_EMAIL = "support@elationemr.com";

export type ProvisioningStatusPollingState = {
  data?: { data?: AuthProvisioningStatus };
  fetchFailureCount: number;
  hasError: boolean;
  isNotFoundError: boolean;
  isNetworkError: boolean;
  pendingElapsedMs?: number;
};

export type ProvisioningFailureView = {
  title: string;
  description: string;
  requestId?: string;
  supportHref: string;
  signInRecommended?: boolean;
};

export function getPendingMessage(elapsedMs: number): string {
  if (elapsedMs < 5_000) return "Creating your account...";
  if (elapsedMs < 15_000) return "Setting up your practice in Elation...";
  if (elapsedMs < 30_000) return "Almost ready - finalizing your account...";
  return "This is taking longer than usual. Hang tight.";
}

export function getNetworkBackoffDelayMs(consecutiveFailures: number): number {
  if (consecutiveFailures <= 0) return PROVISIONING_POLL_INTERVAL_MS;

  return Math.min(
    PROVISIONING_POLL_INTERVAL_MS * 2 ** (consecutiveFailures - 1),
    PROVISIONING_NETWORK_BACKOFF_MAX_MS,
  );
}

export function getProvisioningStatusRefetchInterval({
  data,
  fetchFailureCount,
  hasError,
  isNetworkError,
  isNotFoundError,
  pendingElapsedMs = 0,
}: ProvisioningStatusPollingState): number | false {
  const latestData = data?.data;

  if (latestData?.state === "succeeded" || latestData?.state === "failed") {
    return false;
  }

  if (pendingElapsedMs >= PROVISIONING_PENDING_TIMEOUT_MS) {
    return false;
  }

  if (hasError) {
    if (isNotFoundError) return false;
    if (isNetworkError) return getNetworkBackoffDelayMs(fetchFailureCount);
    return false;
  }

  return PROVISIONING_POLL_INTERVAL_MS;
}

export function buildProvisioningSupportHref(requestId?: string): string {
  const params = new URLSearchParams();
  params.set("subject", "Stitch account provisioning issue");
  params.set(
    "body",
    requestId
      ? `Reference: ${requestId}`
      : "I need help finishing account setup.",
  );

  return `mailto:${PROVISIONING_SUPPORT_EMAIL}?${params.toString()}`;
}

export function getProvisioningFailureView(
  status: AuthProvisioningStatus,
): ProvisioningFailureView {
  const requestId = status.requestId;
  const base = {
    requestId,
    supportHref: buildProvisioningSupportHref(requestId),
  };
  const failureCode = status.failureReason?.code;

  if (failureCode === "user_already_exists") {
    return {
      ...base,
      title: "This email is already registered with Elation.",
      description: "Sign in instead or use a different email.",
      signInRecommended: true,
    };
  }

  if (failureCode === "validation_error") {
    return {
      ...base,
      title: "Account setup failed",
      description:
        "We couldn't validate your information. Please contact support and reference the ID below.",
    };
  }

  return {
    ...base,
    title: "Account setup failed",
    description:
      "Something went wrong while setting up your account. Please contact support and reference the ID below.",
  };
}
